import { Resolver, SubmitHandler, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Substation, SubstationUsina } from "../types";
import { useContext, useEffect, useState } from "react";
import { Grid, Switch, Typography } from "@mui/material";
import { Accordion } from "../../../main/components/accordion/Accordion";
import TextInput from "../../../main/components/TextInput";
import { MainContext } from "../../../main/context/MainContextProvider";
import BaseButton from "../../../main/components/BaseButton";
import DangerBaseButton from "../../../main/components/DangerBaseButton";
import { usinasApi } from "../../../services/api";
import { ConfirmationModal } from "../../../main/components/ConfirmationModal";
import { ActionAlert } from "../../../main/components/alerts/ActionAlert";

const schema = yup.object({
    hasSubstation: yup.boolean().optional().nullable(),
    hasTransformer: yup.boolean().optional().nullable(),
    hasDisjuntor: yup.boolean().optional().nullable(),
    disjuntorTag: yup.string().optional().nullable(),
});

type MyResolver = Resolver<yup.InferType<typeof schema>>;

interface EditSubstationUsinaProps {
    substation: Substation | undefined
    onMount: () => void;
    usinaId: string;
}

const EditSubstationUsina: React.FC<EditSubstationUsinaProps> = ({
    substation,
    onMount,
    usinaId
}) => {
    const { theme, userIsAdm, userIsClient } = useContext(MainContext);
    const primary = theme.muiTheme.palette.primary.main;

    const [hasTransformer, setHasTransformer] = useState(true);
    const [hasSubstation, setHasSubstation] = useState(true);
    const [hasDisjuntor, setHasDisjuntor] = useState(substation?.hasDisjuntor ?? false);

    const {
        handleSubmit,
        control,
        setValue,
        formState: { errors }
    } = useForm({
        defaultValues: {
            disjuntorTag: substation?.disjuntorTag,
            hasTransformer: substation?.hasTransformer,
            hasSubstation: substation?.hasSubstation,
            hasDisjuntor: substation?.hasDisjuntor ?? false,
        },
        resolver: yupResolver(schema) as MyResolver,
    });

    const [isSaveConfirmationOpen, setSaveConfirmationOpen] = useState(false);
    const [submitData, setSubmitData] = useState<SubstationUsina | null>(null);

    const onSubmit: SubmitHandler<SubstationUsina> = async (data) => {
        setSubmitData(data);
        setSaveConfirmationOpen(true);
    };

    const handleSaved = async (data: SubstationUsina | null) => {
        if (
            substation?.hasSubstation !== data?.hasSubstation ||
            substation?.hasTransformer !== data?.hasTransformer ||
            substation?.hasDisjuntor !== data?.hasDisjuntor ||
            substation?.disjuntorTag !== data?.disjuntorTag
        ) {
            try {
                await usinasApi.patchUsina(usinaId, {
                    substation: {
                        hasSubstation: data?.hasSubstation,
                        hasTransformer: data?.hasTransformer,
                        hasDisjuntor: data?.hasDisjuntor,
                        disjuntorTag: data?.disjuntorTag,
                        commands: [],
                    },
                });
                onMount();
            } catch (error) {
                console.error("Erro ao atualizar subestação:", error);
            } finally {
                setIsEditing(false);
            }
        }
    };

    useEffect(() => {
        setValue(`hasTransformer`, hasTransformer);
    }, [hasTransformer]);

    useEffect(() => {
        setValue(`hasSubstation`, hasSubstation);
    }, [hasSubstation]);

    useEffect(() => {
        setValue(`hasDisjuntor`, hasDisjuntor);
    }, [hasDisjuntor]);

    useEffect(() => {
        setValue(`disjuntorTag`, substation?.disjuntorTag ?? "");
        setHasTransformer(substation?.hasTransformer ?? false);
        setHasSubstation(substation?.hasSubstation ?? false);
        setHasDisjuntor(substation?.hasDisjuntor ?? false);
    }, []);

    const [isSubstationVisible, setIsSubstationVisible] = useState(false);

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [openSuccessModal, setOpenSuccessModal] = useState(false);
    const [openErrorModal, setOpenErrorModal] = useState(false);

    return (
        <Grid sx={{ marginBottom: "30px" }}>
            <Accordion.Root>
                <Accordion.Header
                    toggleDetails={() => setIsSubstationVisible(!isSubstationVisible)}
                    showDetails={isSubstationVisible}
                    title={"SUB"}
                    disabled={!hasSubstation}
                    typographySx={{ fontSize: { xs: "13px", sm: "16px" }, marginTop: "6px" }}
                    iconSx={{ marginTop: "6px" }}
                >
                    <>
                        <Grid item display={"flex"} sx={{ alignItems: "center", marginLeft: "24px", columnGap: "10px" }}>

                            <Typography variant="subtitle1" sx={{}}>
                                A Usina possui Subestação?
                            </Typography>
                            Não
                            <Switch
                                checked={hasSubstation}
                                onChange={(_, checked) => {
                                    setHasSubstation(checked);
                                }}
                            />
                            Sim
                        </Grid>
                    </>
                </Accordion.Header>
                <Accordion.Content
                    showDetails={isSubstationVisible}
                    sx={{}}
                >
                    <Grid container sx={{

                    }}>
                        {(userIsAdm || userIsClient) && (
                            <BaseButton
                                title="Editar"
                                onClick={() => setIsEditing(true)}
                                sx={{
                                    backGroundColor: primary,
                                    borderColor: primary,
                                    "& .MuiTypography-root": {
                                        color: primary,
                                    },
                                    "&:hover": {
                                        borderColor: primary,
                                        backgroundColor: primary,
                                        color: primary,
                                        "& .MuiTypography-root": {
                                            color: "#FFFFFF",
                                        },
                                    },
                                    marginLeft: "auto",
                                    width: "12%",
                                    height: "30px",
                                }}
                            />
                        )}
                        <Grid
                            item
                            xs={12}
                            sx={{
                                display: "flex",
                                padding: "20px",
                                flexDirection: "column",
                                backgroundColor: "rgba(217, 217, 217, 0.1)",
                                borderRadius: 2,
                                marginTop: "8px",
                                marginBottom: "8px",
                                rowGap: "16px ",
                                width: "100%",
                            }}
                        >
                            <Grid item xs={6} textAlign={"start"}>
                                <Typography>Subestação</Typography>
                            </Grid>

                            <Grid container spacing={2}>
                                <Grid
                                    container
                                    spacing={2}
                                    sx={{
                                        padding: "20px",
                                    }}
                                // onSubmit={handleSubmit(onSubmit)}
                                >
                                    <>
                                        <Grid sx={{ display: "flex", alignItems: "flex-end" }} item xs={12} md={4}>
                                            <TextInput
                                                control={control}
                                                fieldName="disjuntorTag"
                                                label={"Tag do disjuntor"}
                                                errors={errors}
                                                disabled={!isEditing || !hasDisjuntor}
                                                sx={{
                                                    width: "100%",
                                                    marginBottom: "10px",
                                                }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={4}>
                                            <Grid
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    rowGap: "8px",
                                                    maxWidth: "210px",
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                        marginTop: "15px",
                                                        minHeight: "56px",
                                                        lineHeight: 1.25,
                                                    }}
                                                >
                                                    A Subestação possui disjuntor?
                                                </Typography>
                                                <Grid sx={{ display: "flex", alignItems: "center", justifyContent: "center", columnGap: "8px", whiteSpace: "nowrap" }}>
                                                    <Typography component="span">Não</Typography>
                                                    <Switch
                                                        checked={hasDisjuntor}
                                                        disabled={!isEditing}
                                                        onChange={(_, checked) => {
                                                            setHasDisjuntor(checked);
                                                        }}
                                                    />
                                                    <Typography component="span">Sim</Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid item xs={12} md={4}>
                                            <Grid
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    rowGap: "8px",
                                                    maxWidth: "210px",
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                        marginTop: "15px",
                                                        minHeight: "56px",
                                                        lineHeight: 1.25,
                                                    }}
                                                >
                                                    A Subestação possui transformador?
                                                </Typography>
                                                <Grid sx={{ display: "flex", alignItems: "center", justifyContent: "center", columnGap: "8px", whiteSpace: "nowrap" }}>
                                                    <Typography component="span">Não</Typography>
                                                    <Switch
                                                        checked={hasTransformer}
                                                        disabled={!isEditing}
                                                        onChange={(_, checked) => {
                                                            setHasTransformer(checked);
                                                        }}
                                                    />
                                                    <Typography component="span">Sim</Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </>
                                </Grid>
                            </Grid>
                        </Grid>

                        {(userIsAdm || userIsClient) && isEditing && (
                            <Grid
                                item
                                xs={12}
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    alignItems: "center",
                                }}
                            >
                                <BaseButton
                                    title="Salvar"
                                    onClick={handleSubmit(onSubmit)}
                                    sx={{
                                        backGroundColor: primary,
                                        borderColor: primary,
                                        "& .MuiTypography-root": {
                                            color: primary,
                                        },
                                        "&:hover": {
                                            borderColor: primary,
                                            backgroundColor: primary,
                                            color: primary,
                                            "& .MuiTypography-root": {
                                                color: "#FFFFFF",
                                            },
                                        },
                                        marginRight: "8px",
                                        width: "10%",
                                        height: "30px",
                                    }}
                                />
                                <DangerBaseButton
                                    title="Cancelar"
                                    onClick={() => setIsEditing(false)}
                                    sx={{
                                        width: "10%",
                                        height: "30px",
                                    }}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Accordion.Content>
            </Accordion.Root>
            <ConfirmationModal
                open={isSaveConfirmationOpen}
                setOpen={() => setSaveConfirmationOpen(false)}
                title="Editar Subestação"
                contentText="Deseja confirmar a edição da Subestação?"
                onConfirm={() => handleSaved(submitData)}
                setOpenErrorModal={setOpenErrorModal}
                setOpenSuccessModal={setOpenSuccessModal}
            />
            <ActionAlert
                message="Ocorreu um erro ao editar a UG! Tente novamente mais tarde."
                severity="error"
                open={openErrorModal}
                setOpen={setOpenErrorModal}
            />
            <ActionAlert
                message="A edição da UG foi realizada com sucesso!"
                severity="success"
                open={openSuccessModal}
                setOpen={() => setOpenSuccessModal(false)}
            />
        </Grid>
    );
};

export default EditSubstationUsina;
